import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Table, Input, Dropdown } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { PanelStatus, TProviderPanelGroup } from 'types/credentialing';
import dayjs from 'utils/dayjs';
import { mapApiBucketToFrontend, mapApiStatusToCred, BUCKET_ORDER } from '../../statusMeta';
import StatusBadge from '../StatusBadge';
import { canWithdraw, canResubmit } from '../SoloCredentialingRecordTable';
import s from './s.module.less';

const SvgSearch = () => (
    <svg
        viewBox="0 0 24 24"
        width="15"
        height="15"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <circle cx="11" cy="11" r="7" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);

type ProviderSummaryRow = {
    rowType: 'provider';
    key: string;
    providerId: number;
    name: string;
    owner: boolean;
    summary: string;
    panels: PanelStatus[];
    expanded: boolean;
};

type PanelDetailRow = {
    rowType: 'panel';
    key: string;
    name: string;
    state?: string;
    submitted?: string;
    updated?: string;
    status: PanelStatus['status'];
    statusBucket: PanelStatus['statusBucket'];
    statusDisplay?: string;
    contractSigned?: boolean;
    // Withdraw/Resubmit 行操作定位
    klarityPayerId?: number;
    providerId?: number;
    apiStatus?: string;
    payerActive?: boolean;
    depositAmountCents?: number;
};

type FlatRow = ProviderSummaryRow | PanelDetailRow;

interface Props {
    practiceGroups: TProviderPanelGroup[];
    currentProviderId?: number;
    isMultiState?: boolean;
    // 不传则不渲染 Actions 列（team provider 只读视图）
    onWithdraw?: (row: PanelStatus) => void;
    onResubmit?: (row: PanelStatus) => void;
}

const summarizePanels = (panelList: PanelStatus[]) => {
    const actionCount = panelList.filter((p) => p.statusBucket === 'action').length;
    const panelText = `${panelList.length} panel${panelList.length !== 1 ? 's' : ''}`;
    if (actionCount > 0) {
        return `${panelText}  ·  ${actionCount} action needed`;
    }
    return panelText;
};

const formatDate = (val?: string) => {
    if (!val) return '-';
    const d = dayjs(val);
    return d.isValid() ? d.format('MM/DD/YYYY') : val;
};

const GroupCredentialingRecordTable = ({
    practiceGroups,
    currentProviderId,
    isMultiState,
    onWithdraw,
    onResubmit,
}: Props) => {
    const showActions = !!(onWithdraw || onResubmit);
    const [searchText, setSearchText] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [expandedIds, setExpandedIds] = useState<number[]>([]);

    const searchRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!searchOpen) return undefined;
        const handler = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
                setSearchOpen(false);
            }
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [searchOpen]);

    const toggleExpand = (providerId: number) => {
        setExpandedIds((prev) =>
            prev.includes(providerId) ? prev.filter((id) => id !== providerId) : [...prev, providerId],
        );
    };

    const dataSource: FlatRow[] = useMemo(() => {
        let providers = practiceGroups
            .filter((g) => g.panelTotal > 0)
            .map((g) => {
                const name = `${g.firstName} ${g.lastName}`;
                const panels: PanelStatus[] = (g.panels || []).map((row) => ({
                    name: row.payerName,
                    klarityPayerId: row.klarityPayerId,
                    providerId: g.providerId,
                    apiStatus: row.status,
                    status: mapApiStatusToCred(row.status, row.statusBucket),
                    statusBucket: mapApiBucketToFrontend(row.statusBucket),
                    statusDisplay: row.statusDisplay,
                    state: row.payerState,
                    submitted: row.providerSubmittedAt || row.firstSubmittedToPayerAt,
                    updated: row.lastUpdatedAt,
                    contractSigned: !!row.payerContractSignedAt,
                    payerActive: row.payerActive,
                    depositAmountCents: row.depositAmountCents,
                }));
                panels.sort((a, b) => BUCKET_ORDER.indexOf(a.statusBucket) - BUCKET_ORDER.indexOf(b.statusBucket));
                return { providerId: g.providerId, name, owner: g.self, panels };
            });

        if (searchText) {
            const q = searchText.toLowerCase();
            providers = providers.filter((p) => p.name.toLowerCase().includes(q));
        }

        const rows: FlatRow[] = [];
        providers.forEach((p) => {
            const expanded = expandedIds.includes(p.providerId);
            rows.push({
                rowType: 'provider',
                key: `provider-${p.providerId}`,
                providerId: p.providerId,
                name: p.name,
                owner: p.owner,
                summary: summarizePanels(p.panels),
                panels: p.panels,
                expanded,
            });
            if (expanded) {
                p.panels.forEach((panel, i) => {
                    rows.push({
                        rowType: 'panel',
                        key: `panel-${p.providerId}-${i}`,
                        name: panel.name,
                        klarityPayerId: panel.klarityPayerId,
                        providerId: panel.providerId,
                        apiStatus: panel.apiStatus,
                        state: panel.state,
                        submitted: panel.submitted,
                        updated: panel.updated,
                        status: panel.status,
                        statusBucket: panel.statusBucket,
                        statusDisplay: panel.statusDisplay,
                        contractSigned: panel.contractSigned,
                        payerActive: panel.payerActive,
                        depositAmountCents: panel.depositAmountCents,
                    });
                });
            }
        });
        return rows;
    }, [practiceGroups, searchText, expandedIds]);

    const columns: ColumnsType<FlatRow> = [
        {
            title: (
                <div className={s.providerTh} ref={searchRef}>
                    <span>Provider</span>
                    <span
                        className={`${s.searchBtn}${searchText ? ` ${s.active}` : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSearchOpen(!searchOpen);
                        }}
                    >
                        <SvgSearch />
                    </span>
                    {searchOpen && (
                        <div className={s.searchMenu} onClick={(e) => e.stopPropagation()}>
                            <Input
                                placeholder={`Search ${practiceGroups.length} providers`}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                allowClear
                                size="small"
                            />
                        </div>
                    )}
                </div>
            ),
            dataIndex: 'name',
            width: '25%',
            onCell: (row) => {
                if (row.rowType === 'provider') return {};
                return {};
            },
            render: (_: string, row: FlatRow) => {
                if (row.rowType === 'provider') {
                    return (
                        <span className={s.providerCell} onClick={() => toggleExpand(row.providerId)}>
                            <span className={`${s.expandIcon}${row.expanded ? ` ${s.expanded}` : ''}`}>
                                <svg
                                    viewBox="0 0 20 20"
                                    width="14"
                                    height="14"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <polyline points="8 5 13 10 8 15" />
                                </svg>
                            </span>
                            {row.name}
                            {row.providerId === currentProviderId && <span className={s.youTag}>You</span>}
                        </span>
                    );
                }
                return null;
            },
        },
        {
            title: 'Payer',
            dataIndex: 'payer',
            onCell: (row) => {
                if (row.rowType === 'provider') return { colSpan: showActions ? 5 : 4 };
                return {};
            },
            render: (_: unknown, row: FlatRow) => {
                if (row.rowType === 'provider') {
                    return <span className={s.summaryCell}>{row.summary}</span>;
                }
                return (
                    <span className={s.panelPayer}>
                        {isMultiState && row.state ? `${row.name} - ${row.state}` : row.name}
                    </span>
                );
            },
        },
        {
            title: 'Submitted',
            dataIndex: 'submitted',
            onCell: (row) => {
                if (row.rowType === 'provider') return { colSpan: 0 };
                return {};
            },
            render: (_: unknown, row: FlatRow) => {
                if (row.rowType === 'panel') {
                    return <span className={s.panelDate}>{formatDate(row.submitted)}</span>;
                }
                return null;
            },
        },
        {
            title: 'Last updated',
            dataIndex: 'updated',
            onCell: (row) => {
                if (row.rowType === 'provider') return { colSpan: 0 };
                return {};
            },
            render: (_: unknown, row: FlatRow) => {
                if (row.rowType === 'panel') {
                    return <span className={s.panelDate}>{formatDate(row.updated)}</span>;
                }
                return null;
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            onCell: (row) => {
                if (row.rowType === 'provider') return { colSpan: 0 };
                return {};
            },
            render: (_: unknown, row: FlatRow) => {
                if (row.rowType === 'panel') {
                    return (
                        <span className={s.panelStatus}>
                            {row.statusDisplay ? (
                                <StatusBadge
                                    status={row.status}
                                    displayText={row.statusDisplay}
                                    contractSigned={row.contractSigned}
                                />
                            ) : (
                                <StatusBadge status={row.status} contractSigned={row.contractSigned} />
                            )}
                        </span>
                    );
                }
                return null;
            },
        },
    ];

    // Withdraw/Resubmit 行操作列：仅 panel 行渲染；provider 头行由首列 colSpan 吞掉本列
    if (showActions) {
        columns.push({
            title: '',
            key: 'actions',
            width: 56,
            onCell: (row) => {
                if (row.rowType === 'provider') return { colSpan: 0 };
                return {};
            },
            render: (_: unknown, row: FlatRow) => {
                if (row.rowType !== 'panel') return null;
                const panel = row as unknown as PanelStatus;
                const items = [];
                if (canWithdraw(panel) && onWithdraw) {
                    // 不用 danger（hover 会变成实心红底白字），红字 + 默认灰底 hover
                    items.push({
                        key: 'withdraw',
                        label: <span style={{ color: '#DC2626' }}>Withdraw</span>,
                        onClick: () => onWithdraw(panel),
                    });
                }
                if (canResubmit(panel) && onResubmit) {
                    items.push({ key: 'resubmit', label: 'Resubmit', onClick: () => onResubmit(panel) });
                }
                if (!items.length) return null;
                return (
                    <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
                        <button type="button" className={s.actionsBtn} aria-label="Panel actions">
                            ⋯
                        </button>
                    </Dropdown>
                );
            },
        });
    }

    return (
        <div className={s.wrap}>
            <Table<FlatRow>
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                rowClassName={(row) => (row.rowType === 'provider' ? s.providerRow : s.panelRow)}
                locale={{
                    emptyText: (
                        <div className={s.empty}>
                            {searchText ? 'No providers match your search.' : 'No panels yet.'}
                        </div>
                    ),
                }}
            />
        </div>
    );
};

export default GroupCredentialingRecordTable;
